import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown, ChevronUp } from 'lucide-react';
import * as Icons from 'lucide-react';
import { Card } from '../ui/Card';
import { AppState, ModuleId } from '../../types';
import { GroupId } from '../../config/systemConfig';
import { MODULES } from '../../data/questionLoader';
import { Badge } from '../ui/Badge';
interface StudentData {
  id: string;
  name: string;
  data: AppState;
}
interface TeacherSubjectsTabProps {
  students: StudentData[];
  selectedGroup: GroupId;
  getModuleMastery: (learnerId: string, moduleId: ModuleId) => number;
}
export function TeacherSubjectsTab({
  students,
  selectedGroup,
  getModuleMastery
}: TeacherSubjectsTabProps) {
  const [expandedModuleId, setExpandedModuleId] = useState<ModuleId | null>(null);
  const subjectData = MODULES.map(module => {
    let totalMastery = 0;
    const distribution = {
      excellent: [] as {
        name: string;
        mastery: number;
      }[],
      good: [] as {
        name: string;
        mastery: number;
      }[],
      needsAttention: [] as {
        name: string;
        mastery: number;
      }[]
    };
    students.forEach(student => {
      const mastery = getModuleMastery(student.id, module.id);
      totalMastery += mastery;
      if (mastery >= 85) {
        distribution.excellent.push({
          name: student.name,
          mastery
        });
      } else if (mastery >= 40) {
        distribution.good.push({
          name: student.name,
          mastery
        });
      } else {
        distribution.needsAttention.push({
          name: student.name,
          mastery
        });
      }
    });
    // Sort each group by mastery descending
    distribution.excellent.sort((a, b) => b.mastery - a.mastery);
    distribution.good.sort((a, b) => b.mastery - a.mastery);
    distribution.needsAttention.sort((a, b) => b.mastery - a.mastery);
    const avgMastery = students.length > 0 ? Math.round(totalMastery / students.length) : 0;
    return {
      module,
      avgMastery,
      distribution
    };
  });
  return <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-5374">
      {subjectData.map(({
      module,
      avgMastery,
      distribution
    }, index) => {
      const isExpanded = expandedModuleId === module.id;
      return <motion.div key={module.id} initial={{
        opacity: 0,
        y: 10
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: index * 0.05
      }} data-id="element-5375">
            <Card className="overflow-hidden border-slate-200" data-id="element-5376">
              <div className="p-5 cursor-pointer hover:bg-slate-50 transition-colors flex items-center justify-between" onClick={() => setExpandedModuleId(isExpanded ? null : module.id)} data-id="element-5377">
                <div className="flex items-center gap-4" data-id="element-5378">
                  <div className="w-12 h-12 rounded-xl bg-indigo-50 flex items-center justify-center" data-id="element-5379">
                    {(() => {
                  const IconComp = (Icons as any)[module.icon] || Icons.BookOpen;
                  return <IconComp className="w-6 h-6 text-indigo-600" data-id="element-5380" />;
                })()}
                  </div>
                  <div data-id="element-5381">
                    <h3 className="font-bold text-slate-900" data-id="element-5382">{module.title}</h3>
                    <div className="text-sm text-slate-500 font-medium" data-id="element-5383">
                      Klasgemiddelde: {avgMastery}%
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-3" data-id="element-5384">
                  <div className="w-16 bg-slate-100 rounded-full h-2 overflow-hidden hidden sm:block" data-id="element-5385">
                    <div className={`h-full rounded-full ${avgMastery >= 80 ? 'bg-emerald-500' : avgMastery >= 50 ? 'bg-amber-500' : 'bg-rose-500'}`} style={{
                  width: `${avgMastery}%`
                }} data-id="element-5386" />
                  </div>
                  {isExpanded ? <ChevronUp className="w-5 h-5 text-slate-400" data-id="element-5387" /> : <ChevronDown className="w-5 h-5 text-slate-400" data-id="element-5388" />}
                </div>
              </div>

              <AnimatePresence data-id="element-5389">
                {isExpanded && <motion.div initial={{
              height: 0,
              opacity: 0
            }} animate={{
              height: 'auto',
              opacity: 1
            }} exit={{
              height: 0,
              opacity: 0
            }} className="border-t border-slate-100 bg-slate-50/50" data-id="element-5390">
                    <div className="p-5 space-y-4" data-id="element-5391">
                      <DistributionSection title="Uitstekend (≥85%)" color="emerald" students={distribution.excellent} data-id="element-5392" />
                      <DistributionSection title="Goed Bezig (40-84%)" color="indigo" students={distribution.good} data-id="element-5393" />
                      <DistributionSection title="Extra Aandacht (<40%)" color="rose" students={distribution.needsAttention} data-id="element-5394" />
                    </div>
                  </motion.div>}
              </AnimatePresence>
            </Card>
          </motion.div>;
    })}
    </div>;
}
function DistributionSection({
  title,
  color,
  students
}: {
  title: string;
  color: 'emerald' | 'indigo' | 'rose';
  students: {
    name: string;
    mastery: number;
  }[];
}) {
  if (students.length === 0) return null;
  const colorClasses = {
    emerald: 'text-emerald-700 bg-emerald-100 border-emerald-200',
    indigo: 'text-indigo-700 bg-indigo-100 border-indigo-200',
    rose: 'text-rose-700 bg-rose-100 border-rose-200'
  };
  return <div data-id="element-5395">
      <h4 className={`text-xs font-bold uppercase tracking-wider mb-2 text-${color}-600`} data-id="element-5396">
        {title}
      </h4>
      <div className="flex flex-wrap gap-2" data-id="element-5397">
        {students.map((s, i) => <Badge key={i} variant="neutral" className={`border ${colorClasses[color]}`} data-id="element-5398">
            {s.name} <span className="opacity-70 ml-1" data-id="element-5399">{s.mastery}%</span>
          </Badge>)}
      </div>
    </div>;
}